<template>
    <div class="mb-6">
        <page-headline>
            {{$t('pages.auctions.index.auctions')}}
        </page-headline>
        <div class="flex justify-between mb-2 bg-gray-200 p-3">
            <div class="flex justify-end items-center">
                <search-filter v-model="form.search" class="w-full max-w-md mr-4" @reset="reset">
                    <label class="block py-1 pl-3 bg-ezdBlue text-white">{{$t('pages.auctions.index.status')}}</label>
                    <select v-model="form.status" class="w-full form-select" multiple>
                        <option value="1">{{$t('shared.auction_status.planned')}}</option>
                        <option value="2">{{$t('shared.auction_status.preview')}}</option>
                        <option value="3">{{$t('shared.auction_status.running')}}</option>
                        <option value="4">{{$t('shared.auction_status.trailing')}}</option>
                        <option value="5">{{$t('shared.auction_status.finished')}}</option>
                        <option value="6">{{$t('shared.auction_status.cancelled')}}</option>
                        <option value="7">{{$t('shared.auction_status.parked')}}</option>
                    </select>
                    <label class="mt-2 block py-1 pl-3 bg-ezdBlue text-white">{{$t('pages.auctions.index.type')}}</label>
                    <select v-model="form.type" class="w-full form-select">
                        <option value="-1">{{$t('all.auction_types.all')}}</option>
                        <option value="2-7">{{$t('all.auction_types.daily_auction_bid_up')}}</option>
                        <option value="2-4">{{$t('all.auction_types.parallel_auction_bid_up')}}</option>
                        <option value="2-6">{{$t('all.auction_types.online_deals')}}</option>
                        <option value="3-P">{{$t('all.auction_types.custom')}}</option>
                    </select>
                    <label class="mt-2 block py-1 pl-3 bg-ezdBlue text-white">{{$t('all.start_date_from_to')}}</label>
                    <div class="border-2 border-t-0 py-2">
                        <input class="text-gray-700 text-md" type="date" name="from" value="2020-08-25" v-model="form.dateStart"/> - <input class="text-gray-700 text-md" type="date" name="to" v-model="form.dateEnd"/>
                    </div>
                </search-filter>
            </div>
        </div>
        <div class="bg-white shadow overflow-x-auto mt-6">
            <table class="whitespace-nowrap w-full">
                <tr class="text-left table-headline">
                    <th class="px-4 py-2 font-normal">&nbsp;</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.id')}}</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.type')}}</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.ext_id')}}</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.name')}}</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.description')}}</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.article_number')}}</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.start')}}</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.end')}}</th>
                    <th class="px-4 py-2 font-normal">{{$t('pages.auctions.index.auction_columns.bids')}}</th>
                    <th class="px-4 py-2 font-normal" colspan="2">{{$t('pages.auctions.index.auction_columns.orders')}}</th>
                </tr>
                <tr v-if="auctions.data.length || states.hasActiveSearch === true">
                    <td></td>
                    <td class="py-2 px-4">
                        <input style="width: 100px;" :placeholder="$t('pages.auctions.index.search')" v-model="additionalFilters.auctionId" @keyup="watchFilterValue('auctionId')" @keypress.enter="applyFilter( 'auctionId', $event)" class="flex border border-ezdBlue text-sm bg-gray-300 py-1 px-2" type="text"/>
                    </td>
                    <td></td>
                    <td class="py-2 px-4">
                        <input style="width: 100px;" :placeholder="$t('pages.auctions.index.search')" v-model="additionalFilters.externalId" @keyup="watchFilterValue('externalId')" @keypress.enter="applyFilter( 'externalId', $event)" class="border border-ezdBlue text-sm bg-gray-300 py-1 px-2" type="text"/>
                    </td>
                    <td></td>
                    <td></td>
                    <td class="py-2 px-4">
                        <input style="width: 100px;" :placeholder="$t('pages.auctions.index.search')" v-model="additionalFilters.articleId" @keyup="watchFilterValue('articleId')" @keypress.enter="applyFilter( 'articleId', $event)" class="block border border-ezdBlue text-sm bg-gray-300 py-1 px-2" type="text"/>
                    </td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td colspan="2"></td>
                </tr>
                <tr v-for="auction in auctions.data" :key="auction.id" class="hover:bg-gray-300 even:bg-gray-200 text-sm">
                    <td class="border-t">
                        <Link v-if="auction.auction_type_id === 3" class="px-5 py-3 flex items-center" :href="route('auctions.control', { auction: auction.id,  _query: {'view': 'control' }})">
                            <status :data="getAuctionState(auction)"/>
                        </Link>
                        <Link v-else class="px-6 py-4 flex items-center">
                            <status :data="getAuctionState(auction)"/>
                        </Link>
                    </td>
                    <td class="border-t"><a class="px-4 py-3 flex items-center">{{ auction.id }}</a></td>
                    <td class="border-t"><a class="px-4 py-3 flex items-center">{{ auction.auction_type.name }} <span class="ml-1" v-if="auction.type">({{ auction.type }})</span></a></td>
                    <td class="border-t"><a class="px-4 py-3 flex items-center">{{ auction.external_id }}</a></td>
                    <td class="border-t"><a class="px-4 py-3 flex items-center">{{ auction.name }}</a></td>
                    <td class="border-t"><a class="px-4 py-3 flex items-center">{{ auction.description }}</a></td>
                    <td class="border-t"><a class="px-4 py-3 flex items-center">{{ auction.article_id }}</a></td>
                    <td class="border-t">
                        <a class="px-4 py-3 flex items-center">
                            <span class="block">{{ (auction.date_started === null ? auction.date_start : auction.date_started) | formatDate }}</span>
                        </a>
                    </td>
                    <td class="border-t">
                        <a class="px-4 py-3 flex items-center">
                            <span class="block">{{ auction.date_end | formatDate }}</span>
                        </a>
                    </td>
                    <td class="border-t"><a class="px-4 py-3 flex items-center">{{ auction.bids.length }}</a></td>
                    <td class="border-t"><a class="px-4 py-3 flex items-center">{{ auction.orders.length }}</a></td>
                    <td class="border-t w-px">
                        <Link
                                class="px-4 flex items-center"
                                :href="route('auctions.info', auction.id)"
                                tabindex="-1"
                        >
                            <icon :name="'eye'" class="block w-6 h-6 fill-gray-400" />
                        </Link>
                    </td>
                </tr>
                <tr v-if="auctions.data.length === 0">
                    <td class="border-t px-4 py-3 text-center" colspan="11">{{$t('pages.auctions.index.no_auctions_available')}}</td>
                </tr>
            </table>
        </div>
        <div class="mb-6">
            <pagination :collection="auctions" :url="$page.props.paginate.currentUrl" :params="this.form" />
        </div>
    </div>
</template>

<script>
    import Layout from '@/Shared/Layout';
    import Pagination from '@/Shared/Pagination';
    import Icon from '@/Shared/Icon';
    import PageHeadline from "@/Shared/PageHeadline";
    import Status from '@/Shared/Status';
    import pickBy from 'lodash/pickBy';
    import throttle from 'lodash/throttle';
    import AuctionState from "@/mixins/AuctionState";
    import SearchFilter from "@/Shared/SearchFilter";
    import { Link } from '@inertiajs/vue2';
    export default {
        metaInfo() {
            return {
                title: this.$t('pages.auctions.index.auctions')
            }
        },
        mixins: [AuctionState],
        layout: Layout,
        remember: 'form',
        components: {
            Pagination,
            Icon,
            PageHeadline,
            Status,
            SearchFilter,
            Link
        },
        props: {
            auctions: Object,
            states: Object
        },
        data() {
            return {
                additionalFilters: {
                    articleId: this.states.articleId,
                    externalId: this.states.externalId,
                    auctionId: this.states.auctionId
                },
                form: {
                    status: this.states.states,
                    type: this.states.type,
                    dateStart: this.states.dateStart,
                    dateEnd: this.states.dateEnd
                }
            }
        },
        watch: {
            form: {
                handler: throttle(function() {
                    let query = pickBy(this.form);
                    this.$inertia.get(this.route('auctions', Object.keys(query).length ? query : {}), {}, { preserveState: true })
                }, 150),
                deep: true
            }
        },
        methods: {
            watchFilterValue(filter) {
              if (this.additionalFilters[filter].length === 0) {
                  this.form = {...this.form, [filter]: ''};
              }
            },
            applyFilter(filter, evt) {
                this.form = {...this.form, [filter]: this.additionalFilters[filter]};
            },
            reset() {
                this.form = { status: [2,3], type: -1 }
            }
        }
    }
</script>
